import dayjs, { type Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import calendar from 'dayjs/plugin/calendar';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration, {
  type Duration as DayjsDuration,
} from 'dayjs/plugin/duration';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

dayjs.extend(advancedFormat);
dayjs.extend(calendar);
dayjs.extend(customParseFormat);
dayjs.extend(duration);
dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(quarterOfYear);
dayjs.extend(relativeTime);
dayjs.extend(utc);

/**
 * Class-based wrapper around `dayjs`. Nothing returns a raw dayjs object,
 * so swapping the underlying library means rewriting this file and nothing
 * else. Add a method here when a call site needs one.
 *
 * `DateTime` is immutable: every operation returns a new instance.
 */

export type DateTimeInput =
  string | number | Date | DateTime | null | undefined;

export type DurationUnit =
  | 'milliseconds'
  | 'seconds'
  | 'minutes'
  | 'hours'
  | 'days'
  | 'weeks'
  | 'months'
  | 'years';

export type StartOfUnit = 'second' | 'day' | 'week' | 'month' | 'quarter';

export type CalendarFormats = {
  sameDay: string;
  lastDay: string;
  lastWeek: string;
  sameElse: string;
};

const ISO_WEEK = /^(\d{4})-?W(\d{2})$/;

/** Monday of ISO week `week` in `year`; ISO week 1 holds January 4th. */
function isoWeekStart(year: number, week: number): Dayjs {
  return dayjs(new Date(year, 0, 4))
    .startOf('isoWeek')
    .add(week - 1, 'weeks');
}

/** `dayjs(input)`, also reading ISO week strings ("2025-W10") as moment did. */
function toDayjs(input: DateTimeInput): Dayjs {
  if (input instanceof DateTime) return dayjs(input.valueOf());
  const week = typeof input === 'string' ? ISO_WEEK.exec(input) : null;
  if (week) return isoWeekStart(Number(week[1]), Number(week[2]));
  return dayjs(input);
}

export class DateTime {
  readonly #value: Dayjs;

  private constructor(value: Dayjs) {
    this.#value = value;
  }

  static now(): DateTime {
    return new DateTime(dayjs());
  }

  static utcNow(): DateTime {
    return new DateTime(dayjs.utc());
  }

  /** `undefined` means now, `null` is invalid. */
  static from(input: DateTimeInput): DateTime {
    if (input instanceof DateTime) return input;
    return new DateTime(toDayjs(input));
  }

  /** `format` applies to strings only; anything else is read as by `from`. */
  static parse(input: DateTimeInput, format: string): DateTime {
    if (typeof input !== 'string') return DateTime.from(input);
    return new DateTime(dayjs(input, format));
  }

  static parseUtc(input: string, format: string, strict = false): DateTime {
    return new DateTime(dayjs.utc(input, format, strict));
  }

  /**
   * Monday of week `WW` in `YYYY` (e.g. "2025-11"), the inverse of
   * `format('YYYY-WW')`. Week numbers are ISO.
   */
  static fromYearWeek(yearWeek: string): DateTime {
    const [year, week] = yearWeek.split('-').map(Number);
    return new DateTime(isoWeekStart(year!, week!));
  }

  static fromUnixSeconds(seconds: number): DateTime {
    return new DateTime(dayjs.unix(seconds));
  }

  /** Localised weekday name, 1 = Monday … 7 = Sunday. */
  static isoWeekdayName(isoWeekday: number): string {
    return dayjs().isoWeekday(isoWeekday).format('dddd');
  }

  add(amount: number, unit: DurationUnit): DateTime {
    return new DateTime(this.#value.add(amount, unit));
  }

  subtract(amount: number, unit: DurationUnit): DateTime {
    return new DateTime(this.#value.subtract(amount, unit));
  }

  startOf(unit: StartOfUnit): DateTime {
    return new DateTime(this.#value.startOf(unit));
  }

  endOf(unit: StartOfUnit): DateTime {
    return new DateTime(this.#value.endOf(unit));
  }

  /** Locale weekday, 0 = Sunday … 6 = Saturday. */
  withDayOfWeek(day: number): DateTime {
    return new DateTime(this.#value.day(day));
  }

  /** 0 = January … 11 = December. */
  withMonth(month: number): DateTime {
    return new DateTime(this.#value.month(month));
  }

  /** 0 = January … 11 = December. */
  month(): number {
    return this.#value.month();
  }

  isValid(): boolean {
    return this.#value.isValid();
  }

  isBefore(other: DateTimeInput): boolean {
    return this.#value.isBefore(toDayjs(other));
  }

  isAfter(other: DateTimeInput): boolean {
    return this.#value.isAfter(toDayjs(other));
  }

  isSameOrBefore(other: DateTimeInput): boolean {
    return this.#value.isSameOrBefore(toDayjs(other));
  }

  isSameOrAfter(other: DateTimeInput): boolean {
    return this.#value.isSameOrAfter(toDayjs(other));
  }

  /** `this - other`, truncated to whole units unless `precise`. */
  diff(other: DateTimeInput, unit: DurationUnit, precise = false): number {
    return this.#value.diff(toDayjs(other), unit, precise);
  }

  /** An invalid date formats as "Invalid date", as moment's did. */
  format(pattern: string): string {
    if (!this.isValid()) return 'Invalid date';
    return this.#value.format(pattern);
  }

  fromNow(): string {
    return this.#value.fromNow();
  }

  /** Relative label (e.g. "Today") against now, falling back to a pattern. */
  calendar(formats: CalendarFormats): string {
    return this.#value.calendar(null, formats);
  }

  toDate(): Date {
    return this.#value.toDate();
  }

  /** UTC with a `Z`, or local time with its offset when `keepOffset`. */
  toISOString(keepOffset = false): string {
    if (keepOffset) return this.#value.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
    return this.#value.toISOString();
  }

  /** Epoch milliseconds. */
  valueOf(): number {
    return this.#value.valueOf();
  }
}

export class Duration {
  readonly #value: DayjsDuration;

  private constructor(value: DayjsDuration) {
    this.#value = value;
  }

  static of(amount: number, unit: DurationUnit): Duration {
    return new Duration(dayjs.duration(amount, unit));
  }

  asSeconds(): number {
    return this.#value.asSeconds();
  }

  asMinutes(): number {
    return this.#value.asMinutes();
  }

  asHours(): number {
    return this.#value.asHours();
  }

  asDays(): number {
    return this.#value.asDays();
  }

  asWeeks(): number {
    return this.#value.asWeeks();
  }

  asMonths(): number {
    return this.#value.asMonths();
  }

  asYears(): number {
    return this.#value.asYears();
  }
}
